import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, Clock, X, CheckCircle2, CalendarClock,
  MessageSquare, Activity, ArrowRight, Bell, Send,
  Loader2, Zap, User, ChevronRight, ExternalLink, RefreshCw,
  ClipboardEdit,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  clinicalAlerts,
  expiringPAIs,
  caseManagerTasks,
  paiTimelines,
  patientDetails,
} from '@/data/mockCaseManagerData';

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

/* ═══════════════════════════════
   WIDGET: Allerta PAI da Completare
   ═══════════════════════════════ */
function PaiToCompleteAlert() {
  const navigate = useNavigate();
  const patientsToComplete = Object.values(patientDetails).filter(p => p.paiStatus === 'DA_COMPLETARE_CM');
  if (patientsToComplete.length === 0) return null;
  return (
    <div className="flex flex-col gap-3" data-testid="pai-to-complete-alerts">
      {patientsToComplete.map(p => (
        <div
          key={p.id}
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 border-l-4 border-l-amber-500 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          data-testid={`pai-alert-${p.id}`}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center shrink-0">
              <ClipboardEdit className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900 dark:text-amber-200">Azione Richiesta: PAI da completare</p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">Il medico UVMD ha assegnato il paziente <strong>{p.cognome} {p.nome}</strong> richiedendo la definizione del piano interventi.</p>
            </div>
          </div>
          <Button
            size="sm"
            className="gap-2 rounded-xl h-10 px-5 bg-amber-600 hover:bg-amber-700 text-white shadow-sm shrink-0"
            onClick={() => navigate(`/case-manager/paziente/${p.id}`)}
            data-testid={`pai-alert-goto-${p.id}`}
          >
            Vai al Caso <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════
   WIDGET A: Alert Clinici
   ═══════════════════════════════ */
function ClinicalAlerts() {
  const [alerts, setAlerts] = useState(clinicalAlerts);
  const [closingId, setClosingId] = useState(null);
  const [auditText, setAuditText] = useState('');

  const handleClose = () => {
    if (!auditText.trim()) return;
    setAlerts(prev => prev.filter(a => a.id !== closingId));
    toast.success('Alert chiuso', { description: 'Azione documentata nel registro audit.' });
    setClosingId(null);
    setAuditText('');
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="widget-alerts">
      <CardContent className="p-7">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center">
              <Zap className="w-4 h-4 text-red-500" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">Alert Clinici</h2>
          </div>
          <Badge className="bg-red-50 text-red-600 border-0 text-[10px] font-semibold">{alerts.filter(a => !a.letto).length} nuovi</Badge>
        </div>

        <div className="flex flex-col gap-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-xl border transition-all ${
                alert.tipo === 'critico'
                  ? 'bg-red-50/60 border-red-100'
                  : 'bg-amber-50/60 border-amber-100'
              }`}
              data-testid={`alert-${alert.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${alert.tipo === 'critico' ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground">{alert.pazienteNome}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{alert.messaggio}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-[10px] font-bold ${alert.tipo === 'critico' ? 'text-red-600' : 'text-amber-600'}`}>
                        {alert.parametro}: {alert.valore}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{alert.timestamp}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setClosingId(alert.id)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/60 transition-colors flex-shrink-0"
                  data-testid={`alert-close-${alert.id}`}
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
          {alerts.length === 0 && (
            <div className="text-center py-6 text-sm text-muted-foreground">Nessun alert attivo</div>
          )}
        </div>

        {/* Modale Audit */}
        <AnimatePresence>
          {closingId && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={() => setClosingId(null)}
            >
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-card rounded-2xl border border-border shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="audit-modal"
              >
                <div className="p-5 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Azione Intrapresa (Audit/Legal)
                  </h3>
                  <p className="text-[11px] text-muted-foreground mt-1">Documenta l'azione clinica intrapresa per chiudere l'alert.</p>
                </div>
                <div className="p-5">
                  <textarea
                    className="w-full h-28 rounded-xl border border-border bg-muted/20 p-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Descrivere l'azione intrapresa..."
                    value={auditText}
                    onChange={(e) => setAuditText(e.target.value)}
                    data-testid="audit-textarea"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => { setClosingId(null); setAuditText(''); }} data-testid="audit-cancel">Annulla</Button>
                  <Button variant="comfort" size="sm" className="gap-1.5" disabled={!auditText.trim()} onClick={handleClose} data-testid="audit-confirm">
                    <CheckCircle2 className="w-3.5 h-3.5" />Chiudi Alert
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   WIDGET B: Scadenziario PAI
   ═══════════════════════════════ */
function PAIExpiry() {
  const navigate = useNavigate();
  const [pais, setPais] = useState(expiringPAIs);

  const handleOpenRival = (pai) => {
    navigate(`/case-manager/paziente/${pai.pazienteId}`, { state: { openRivalutazione: true } });
  };

  /* handlePlanRival rimosso — unico trigger: "Avvia Rivalutazione" */

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="widget-expiry">
      <CardContent className="p-7">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
              <CalendarClock className="w-4 h-4 text-amber-500" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">Scadenziario PAI</h2>
          </div>
          <Badge className="bg-amber-50 text-amber-600 border-0 text-[10px] font-semibold">{pais.filter(p => !p.rivalutazionePianificata).length} da pianificare</Badge>
        </div>

        <div className="flex flex-col gap-4">
          {pais.map((pai) => (
            <div key={pai.id} className="flex items-center gap-3 p-4 rounded-xl bg-muted/20 border border-border/40 hover:bg-muted/40 transition-colors" data-testid={`expiry-${pai.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-foreground">{pai.pazienteNome}</p>
                  <Badge variant="outline" className="text-[9px]">{pai.cia}</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{pai.tipoPAI}</p>
              </div>
              <div className="text-right flex-shrink-0 flex items-center gap-3">
                <div>
                  <p className={`text-xs font-bold ${pai.giorniRimanenti <= 7 ? 'text-red-500' : 'text-amber-500'}`}>
                    {pai.giorniRimanenti}gg
                  </p>
                  <p className="text-[10px] text-muted-foreground">{pai.scadenza}</p>
                </div>
                {pai.rivalutazionePianificata ? (
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center" data-testid={`expiry-done-${pai.id}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="gap-1.5 rounded-lg text-xs font-semibold h-8 px-3"
                    onClick={() => handleOpenRival(pai)}
                    data-testid={`expiry-rival-${pai.id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Pianifica Rivalutazione
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   WIDGET C: Segnalazioni / Task
   ═══════════════════════════════ */
function TasksWidget() {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);

  const prioritaColors = {
    alta: 'bg-red-50 text-red-600',
    media: 'bg-amber-50 text-amber-600',
    bassa: 'bg-slate-100 text-slate-500',
  };
  const tipoIcons = {
    richiesta: Bell,
    segnalazione: AlertTriangle,
    referto: Activity,
    sistema: CheckCircle2,
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="widget-tasks">
      <CardContent className="p-7">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-indigo-500" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">Segnalazioni e Task</h2>
          </div>
          <Badge className="bg-indigo-50 text-indigo-600 border-0 text-[10px] font-semibold">{caseManagerTasks.filter(t => !t.letto).length} non letti</Badge>
        </div>

        <div className="flex flex-col gap-4">
          {caseManagerTasks.map((task) => {
            const Icon = tipoIcons[task.tipo] || Bell;
            return (
              <div
                key={task.id}
                onClick={() => setSelectedTask(task)}
                className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-sm hover:border-primary/30 ${task.letto ? 'bg-card border-border/40' : 'bg-white border-border'}`}
                data-testid={`task-${task.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-xs font-semibold ${task.letto ? 'text-muted-foreground' : 'text-foreground'}`}>{task.oggetto}</p>
                      <Badge className={`text-[9px] border-0 ${prioritaColors[task.priorita]}`}>{task.priorita}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{task.messaggio}</p>
                    <p className="text-[10px] text-muted-foreground mt-1.5">{task.mittente} · {task.timestamp}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Task Detail Modal */}
        <AnimatePresence>
          {selectedTask && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedTask(null)}
              data-testid="task-modal-overlay"
            >
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-card rounded-2xl border border-border shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="task-modal-panel"
              >
                <div className="p-6 border-b border-border">
                  <div className="flex items-center gap-2 mb-2">
                    {(() => { const Icon = tipoIcons[selectedTask.tipo] || Bell; return <Icon className="w-4 h-4 text-muted-foreground" />; })()}
                    <Badge className={`text-[9px] border-0 ${prioritaColors[selectedTask.priorita]}`}>{selectedTask.priorita}</Badge>
                    {!selectedTask.letto && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </div>
                  <h3 className="text-base font-semibold text-foreground">{selectedTask.oggetto}</h3>
                  <p className="text-[11px] text-muted-foreground mt-1">{selectedTask.mittente} · {selectedTask.timestamp}</p>
                </div>
                <div className="p-6">
                  <p className="text-sm text-foreground leading-relaxed">{selectedTask.messaggio}</p>
                </div>
                <div className="flex items-center justify-between gap-3 p-6 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => setSelectedTask(null)} data-testid="task-modal-close">Chiudi</Button>
                  <div className="flex items-center gap-2">
                    {selectedTask.pazienteId && (
                      <Button
                        size="sm"
                        className="gap-1.5"
                        onClick={() => { setSelectedTask(null); navigate(`/case-manager/paziente/${selectedTask.pazienteId}`); }}
                        data-testid="task-modal-go-patient"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />Vai al Paziente
                      </Button>
                    )}
                    <Button
                      variant="comfort"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => { toast.success('Task gestita'); setSelectedTask(null); }}
                      data-testid="task-modal-mark-done"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />Segna Gestita
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   WIDGET D: Timeline PAI (Gantt)
   ═══════════════════════════════ */
function PAITimeline() {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(1);
  const DAYS_PER_MONTH = [31,28,31,30,31,30,31,31,30,31,30,31];
  const daysInMonth = DAYS_PER_MONTH[selectedMonth];
  const todayDay = selectedMonth === 1 ? 18 : Math.min(12 + ((selectedMonth * 3) % 14), daysInMonth - 2);
  const dynamicTimelines = paiTimelines.map((pai, idx) => {
    if (selectedMonth === 1) return pai;
    const seed = (selectedMonth * 7 + idx * 13) % 100;
    const newStart = Math.max(1, ((pai.inizioPAI + seed) % Math.max(1, daysInMonth - 15)) + 1);
    const newDuration = Math.min(daysInMonth - newStart, Math.max(10, pai.durataGiorni + (seed % 10) - 5));
    const newCompletion = Math.max(5, Math.min(98, pai.percentualeCompletamento + (seed % 40) - 20));
    return { ...pai, inizioPAI: newStart, durataGiorni: newDuration, percentualeCompletamento: newCompletion };
  });
  const dayLabels = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="widget-timeline">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
              <Activity className="w-4 h-4 text-violet-500" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">Panoramica ADI Attivi</h2>
          </div>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="text-[11px] text-muted-foreground font-medium bg-card border border-border/50 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer hover:border-border transition-colors"
            data-testid="month-selector"
          >
            {MESI.map((m, i) => (
              <option key={i} value={i}>{m} 2026</option>
            ))}
          </select>
        </div>

        {/* Day Header */}
        <div className="flex items-end mb-3 ml-[160px]">
          {dayLabels.map((d) => (
            <div key={d} className="flex-1 text-center">
              {d % 5 === 0 || d === 1 ? (
                <span className="text-[9px] text-muted-foreground font-medium">{d}</span>
              ) : (
                <span className="text-[9px] text-transparent">.</span>
              )}
            </div>
          ))}
        </div>

        {/* Gantt Rows */}
        <div className="space-y-2.5" data-testid="gantt-rows">
          {dynamicTimelines.map((pai) => {
            const startPercent = ((pai.inizioPAI - 1) / daysInMonth) * 100;
            const widthPercent = (pai.durataGiorni / daysInMonth) * 100;

            return (
              <div
                key={pai.pazienteId}
                className="flex items-center gap-0 cursor-pointer hover:bg-muted/30 rounded-xl p-1 -m-1 transition-colors"
                onClick={() => navigate(`/case-manager/paziente/${pai.pazienteId}`)}
                data-testid={`gantt-row-${pai.pazienteId}`}
              >
                <div className="w-[160px] flex-shrink-0 pr-3">
                  <p className="text-xs font-semibold text-foreground truncate">{pai.pazienteNome}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge variant="outline" className="text-[8px] px-1.5 py-0">{pai.cia}</Badge>
                    <span className="text-[10px] text-muted-foreground">{pai.tipo}</span>
                  </div>
                </div>
                <div className="flex-1 h-9 bg-muted/30 rounded-lg relative overflow-hidden">
                  {dayLabels.map((d) => (
                    d % 7 === 0 && <div key={d} className="absolute top-0 bottom-0 border-l border-border/30" style={{ left: `${(d / daysInMonth) * 100}%` }} />
                  ))}
                  <div className="absolute top-0 bottom-0 w-px bg-red-400/50 z-10" style={{ left: `${(18 / daysInMonth) * 100}%` }} />
                  <div
                    className="absolute top-1 bottom-1 rounded-lg flex items-center justify-end pr-2 transition-all duration-500"
                    style={{
                      left: `${startPercent}%`,
                      width: `${Math.min(widthPercent, 100 - startPercent)}%`,
                      backgroundColor: `${pai.colore}18`,
                      border: `1.5px solid ${pai.colore}40`,
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-lg opacity-30"
                      style={{ width: `${pai.percentualeCompletamento}%`, backgroundColor: pai.colore }}
                    />
                    <span className="relative text-[9px] font-bold" style={{ color: pai.colore }}>
                      {pai.percentualeCompletamento}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border/40">
          <div className="flex items-center gap-1.5">
            <div className="w-px h-3 bg-red-400/50" />
            <span className="text-[10px] text-muted-foreground">Oggi ({todayDay} {MESI[selectedMonth].slice(0, 3)})</span>
          </div>
          {dynamicTimelines.slice(0, 3).map((p) => (
            <div key={p.pazienteId} className="flex items-center gap-1.5">
              <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: p.colore }} />
              <span className="text-[10px] text-muted-foreground">{p.cia}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   DASHBOARD PRINCIPALE
   ═══════════════════════════════ */
export default function CaseManagerDashboard() {
  return (
    <AppLayout role="case-manager">
      <div className="space-y-6" data-testid="case-manager-dashboard">
        <div>
          <h1 className="text-2xl font-semibold text-foreground" data-testid="cm-title">Cruscotto Operativo</h1>
          <p className="text-sm text-muted-foreground mt-1">Panoramica priorita, scadenze e attivita clinico-logistiche</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'PAI Attivi', value: '17', icon: Activity, color: 'bg-violet-50 text-violet-500' },
            { label: 'Alert Aperti', value: String(clinicalAlerts.filter(a => !a.letto).length), icon: Zap, color: 'bg-red-50 text-red-500' },
            { label: 'PAI in Scadenza', value: String(expiringPAIs.length), icon: CalendarClock, color: 'bg-amber-50 text-amber-500' },
            { label: 'Task Non Letti', value: String(caseManagerTasks.filter(t => !t.letto).length), icon: MessageSquare, color: 'bg-indigo-50 text-indigo-500' },
          ].map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${kpi.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-foreground">{kpi.value}</p>
                    <p className="text-[11px] text-muted-foreground">{kpi.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <PaiToCompleteAlert />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ClinicalAlerts />
          <PAIExpiry />
        </div>

        <PAITimeline />
        <TasksWidget />
      </div>
    </AppLayout>
  );
}
